import { useState } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowRight, Check } from "lucide-react";
import { Reveal, Label } from "@/components/Reveal";
import TopoBackground from "@/components/TopoBackground";
import { MEDIA, BUILDS, CULTURAL_SYSTEMS } from "@/data/content";

export default function WhatWeBuild() {
  const [active, setActive] = useState(0);

  return (
    <div data-testid="what-we-build-page" className="pt-20">
      {/* Intro */}
      <section className="relative overflow-hidden py-28 md:py-40 border-b border-white/5">
        <TopoBackground opacity={0.06} />
        <div className="relative z-10 max-w-5xl mx-auto px-6 md:px-12">
          <motion.div
            initial={{ opacity: 0, y: 24 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 1, ease: [0.22, 1, 0.36, 1] }}
          >
            <Label className="block mb-6">What WisdomKeepers Builds</Label>
            <h1 className="font-display font-light text-4xl sm:text-5xl lg:text-6xl leading-[1.08] text-[#F4F1ED]">
              Knowledge Systems That Strengthen Stewardship
            </h1>
            <p className="mt-8 max-w-2xl text-lg md:text-xl text-[#A8A5A1] leading-relaxed">
              We work alongside communities to design systems where governance comes before
              technology. Knowledge is organised around relationships rather than files.
              People rather than databases. Responsibility rather than ownership.
            </p>
          </motion.div>
        </div>
      </section>

      {/* Builds list */}
      <section className="py-20 md:py-28 border-b border-white/5">
        <div className="max-w-5xl mx-auto px-6 md:px-12">
          <Reveal>
            <p className="wk-label mb-10">The platform supports</p>
          </Reveal>
          <div>
            {BUILDS.map((b, i) => (
              <Reveal key={b} delay={i * 0.04}>
                <div
                  data-testid={`build-item-${i}`}
                  className="group flex items-baseline gap-6 py-6 border-b border-white/10 hover:border-[#D4A373]/40 transition-colors duration-500"
                >
                  <span className="font-display text-lg text-[#D4A373]/50 w-10 shrink-0">
                    {String(i + 1).padStart(2, "0")}
                  </span>
                  <span className="font-display text-2xl md:text-3xl text-[#F4F1ED] group-hover:text-[#D4A373] transition-colors duration-500">
                    {b}
                  </span>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* Cultural Knowledge Systems — sticky feature */}
      <section className="py-24 md:py-32 border-b border-white/5">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <Reveal>
            <Label className="block mb-4">Cultural Knowledge Systems</Label>
            <h2 className="font-display font-light text-3xl sm:text-4xl lg:text-5xl text-[#F4F1ED] max-w-3xl">
              The hero is community governance — not any single tool.
            </h2>
            <p className="mt-6 max-w-2xl text-[#A8A5A1] leading-relaxed">
              We help Traditional Owner groups establish community-governed knowledge
              systems, with the pieces assembled around their protocols and priorities.
            </p>
          </Reveal>

          <div className="mt-14 grid lg:grid-cols-2 gap-10 lg:gap-16 items-start">
            {/* list */}
            <div className="space-y-2">
              {CULTURAL_SYSTEMS.map((s, i) => (
                <button
                  key={s.name}
                  data-testid={`cultural-system-tab-${i}`}
                  onMouseEnter={() => setActive(i)}
                  onClick={() => setActive(i)}
                  className={`w-full text-left p-6 rounded-lg border transition-all duration-500 ${
                    active === i
                      ? "border-[#D4A373]/60 bg-[#101612]"
                      : "border-white/8 hover:border-white/20"
                  }`}
                >
                  <div className="flex items-center gap-4">
                    <span
                      className={`h-2 w-2 rounded-full transition-colors duration-500 ${
                        active === i ? "bg-[#D4A373]" : "bg-white/20"
                      }`}
                    />
                    <h3 className="font-display text-2xl text-[#F4F1ED]">{s.name}</h3>
                  </div>
                  {active === i && (
                    <motion.p
                      initial={{ opacity: 0, height: 0 }}
                      animate={{ opacity: 1, height: "auto" }}
                      className="mt-3 pl-6 text-[#A8A5A1] leading-relaxed"
                    >
                      {s.body}
                    </motion.p>
                  )}
                </button>
              ))}
            </div>

            {/* visual */}
            <div className="lg:sticky lg:top-28">
              <div className="relative aspect-[4/5] rounded-lg overflow-hidden border border-white/8">
                <img
                  src={MEDIA.community}
                  alt="Community-governed knowledge"
                  className="absolute inset-0 h-full w-full object-cover"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-[#050806] via-[#050806]/30 to-transparent" />
                <div className="relative z-10 h-full flex flex-col justify-end p-8">
                  <p className="wk-label mb-2">Governance first</p>
                  <p className="font-display text-2xl md:text-3xl text-[#F4F1ED] leading-snug">
                    Systems that remain theirs — for generations, not projects.
                  </p>
                </div>
              </div>
              <ul className="mt-6 grid grid-cols-2 gap-3">
                {["Healthy Country integration", "SeedKeepers integration"].map((t) => (
                  <li key={t} className="flex items-center gap-2 text-sm text-[#A8A5A1]">
                    <Check size={16} className="text-[#D4A373]" /> {t}
                  </li>
                ))}
              </ul>
            </div>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-24 md:py-32">
        <div className="max-w-4xl mx-auto px-6 md:px-12 text-center">
          <Reveal>
            <h2 className="font-display font-light text-3xl sm:text-4xl lg:text-5xl text-[#F4F1ED]">
              Build a system your community will hold for a thousand years.
            </h2>
            <Link
              to="/partner"
              data-testid="build-cta-link"
              className="mt-10 inline-flex items-center gap-2 px-8 py-4 rounded-md bg-[#D4A373] text-[#050806] font-medium hover:bg-[#E6CCB2] transition-colors duration-500"
            >
              Partner With WisdomKeepers <ArrowRight size={18} />
            </Link>
          </Reveal>
        </div>
      </section>
    </div>
  );
}
